import React from 'react'
import { Link } from 'react-router-dom'
import { AttorneyLayout } from '../components/AttorneyLayout'
import { PageHero } from '../components/PageHero'

// IntakeAI — public product page for Della Intake AI.
// ponytail: wire live pricing/signup data if the offer ships.

const BRIEF_FIELDS = [
  ['Caller & property roles', 'Tenant, landlord, manager, buyer, seller — plus the adverse parties for conflict screening'],
  ['Jurisdiction', 'State, county, and property address'],
  ['Lease type & key dates', 'New lease, renewal, active term, signing dates'],
  ['The matter', "The caller's issue in their own words, categorized"],
  ['Notices & deadlines', 'Written notices received or served, court dates, response deadlines, active proceedings'],
  ['Documents', 'What the caller has — and a secure upload link already sent to them'],
  ['Missing information', 'What intake could not capture, flagged for follow-up'],
  ['Urgency & attorney questions', 'Emergency vs. routine, plus the specific questions that need a lawyer'],
]

function SectionHeading({ kicker, title, body }) {
  return (
    <div className="mx-auto max-w-2xl text-center">
      <p className="text-sm font-bold uppercase tracking-widest text-emerald-600">{kicker}</p>
      <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{title}</h2>
      {body && <p className="mt-4 text-lg leading-relaxed text-slate-600">{body}</p>}
    </div>
  )
}

function StepCard({ n, title, body }) {
  return (
    <div className="relative rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <span className="absolute -top-3 left-6 flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 text-xs font-bold text-white shadow">{n}</span>
      <h3 className="mt-2 text-base font-semibold text-slate-900">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-600">{body}</p>
    </div>
  )
}

export function IntakeAI() {
  return (
    <AttorneyLayout>

      <PageHero
        eyebrow="AI Intake · Powered by LeaseCheck"
        title="Intake AI"
        image="/attorney/della-phone.jpg"
        imageAlt="Della taking a call at her desk, laptop open beside her"
        center
        contentTop
        imagePosition="center 70%"
      />

      {/* Body copy + CTAs moved out of the hero so they don't sit over the laptop in the photo */}
      <section className="bg-white py-16 sm:py-20">
        <div className="mx-auto max-w-2xl px-5 text-center">
          <p className="text-xl font-semibold text-emerald-700">Della is your intake desk. She answers every call, every time.</p>
          <p className="mt-4 text-lg leading-relaxed text-slate-600">Careful questioning, thorough documentation, and the conviction that every caller deserves a structured intake — under your firm's own name, your practice areas, your escalation rules.</p>
          <p className="mt-4 text-sm text-slate-500">Della is an AI assistant — not an attorney. She organizes facts for attorney review and never gives legal advice.</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <img src="/attorney/LeaseCheck Logo resized.png" alt="LeaseCheck" className="h-12 w-auto" />
            <a href="https://intakeai.coloradoleasecheck.com/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 px-6 py-3 font-bold text-white shadow-lg transition hover:from-emerald-400 hover:to-teal-400">Explore Intake AI</a>
          </div>
        </div>
      </section>

      {/* PRACTICE AREAS — mirrors DB&H's "Every matter organized before it reaches your desk" section */}
      <section id="practice" className="bg-emerald-50/60 py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-5">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-sm font-bold uppercase tracking-widest text-emerald-600">Practical Counsel for Property Matters</p>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Every matter organized before it reaches your desk.</h2>
            <p className="mt-4 text-lg leading-relaxed text-slate-600">
              Powered by LeaseCheck, Della's intake screens the caller, prescreens the lease on
              landlord-tenant matters, and captures the parties, clauses, notices, and details that
              matter — then delivers one structured Attorney Intake Brief with the supporting documents
              attached, ready for your review and your practice-management system.
            </p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            <div className="rounded-2xl border border-emerald-100 bg-white p-7 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900">Landlord-Tenant Law</h3>
              <p className="mt-1 text-sm font-semibold italic text-emerald-700">Notices, deposits, repairs, renewals — prescreened by LeaseCheck.</p>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">Lease disputes, security deposits, repairs, notices, renewals, and other matters affecting landlords, tenants, and property managers. LeaseCheck prescreens the lease itself before your review.</p>
            </div>
            <div className="rounded-2xl border border-emerald-100 bg-white p-7 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900">Real Estate Transactions</h3>
              <p className="mt-1 text-sm font-semibold italic text-emerald-700">Reviewed before the closing table.</p>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">Structured intake for purchases, sales, disclosures, leases, property-management agreements, and closing-related concerns.</p>
            </div>
            <div className="rounded-2xl border border-emerald-100 bg-white p-7 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900">Property Disputes</h3>
              <p className="mt-1 text-sm font-semibold italic text-emerald-700">Organize the facts first.</p>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">The documents and details surrounding boundaries, easements, ownership, HOA, contractor, and property-contract disputes.</p>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="mx-auto max-w-6xl px-5 py-16 sm:py-20">
        <div className="mx-auto flex max-w-3xl flex-col items-center justify-center gap-6 sm:flex-row">
          <img src="/attorney/LeaseCheck%20Logo%20resized.png" alt="LeaseCheck" className="h-20 w-auto flex-none" />
          <SectionHeading
            kicker="How it works"
            title="From first ring to ready-for-review"
            body="Every call follows the structured intake your staff would run on their best day — every time, including nights and weekends."
          />
        </div>
        <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          <StepCard n="1" title="Della answers" body="Callers hear a natural, professional voice that identifies itself as your firm's AI intake assistant and asks for consent before any recording." />
          <StepCard n="2" title="Structured screening" body="Role identification, conflict-screening details, jurisdiction, lease type and dates, notices, deadlines, documents on hand, and urgency — one conversational question at a time." />
          <StepCard n="3" title="Documents follow" body="The caller receives a secure upload link for the lease, notices, photos, and correspondence — nothing sensitive is collected by voice. On landlord-tenant matters, LeaseCheck prescreens the uploaded lease for compliance flags." />
          <StepCard n="4" title="Brief in your inbox" body="A reviewing AI checks the intake for completeness, then delivers the Attorney Intake Brief with the transcript and recording attached." />
        </div>
      </section>

      {/* MEET DELLA — brief teaser; the full contact.html-style demonstration lives on /contact */}
      <section className="bg-slate-900 py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-5 text-center">
          <p className="text-sm font-bold uppercase tracking-widest text-emerald-400">The AI Intake Demonstration</p>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">Meet Della</h2>
          <p className="mt-3 text-lg font-semibold text-white">She could be your firm's most consistently available team member.</p>
          <p className="mt-5 text-base leading-relaxed text-white">
            Della is an automated intake assistant — deployed under your firm's own name, she identifies
            the nature of a matter, collects the relevant facts, asks about notices and deadlines,
            identifies supporting documents, and prepares an organized Attorney Intake Brief for your
            review.
          </p>
          <p className="mt-3 text-sm text-emerald-100">
            Della is an AI intake assistant — not an attorney — and does not provide legal advice.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/contact#form"
              className="rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 px-6 py-3 text-base font-bold text-emerald-950 shadow-sm transition hover:from-emerald-400 hover:to-teal-400"
            >
              Request Demo Access
            </Link>
          </div>
        </div>
      </section>

      {/* THE BRIEF */}
      <section id="brief" className="bg-slate-900 py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-5">
          <div className="mx-auto flex max-w-3xl flex-col items-center justify-center gap-6 sm:flex-row">
            <img src="/attorney/LeaseCheck%20Logo%20resized.png" alt="LeaseCheck" className="h-20 w-auto flex-none" />
            <div className="max-w-2xl text-center">
              <p className="text-sm font-bold uppercase tracking-widest text-emerald-400">The deliverable</p>
              <h2 className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">The Attorney Intake Brief</h2>
              <p className="mt-4 text-lg leading-relaxed text-white">
                Not a voicemail. Not a sticky note.<br /> A structured summary of everything a first consultation needs.
              </p>
            </div>
          </div>
          <div className="mx-auto mt-12 max-w-3xl overflow-hidden rounded-2xl border border-slate-700 bg-slate-800 shadow-2xl">
            <div className="border-b border-slate-700 bg-slate-800/80 px-6 py-4">
              <p className="text-sm font-bold uppercase tracking-wider text-emerald-400">Attorney Intake Brief</p>
              <p className="mt-1 text-xs text-white">Prepared by Della &middot; Intake AI &middot; for attorney review</p>
            </div>
            <dl className="divide-y divide-slate-700/70">
              {BRIEF_FIELDS.map(([term, detail]) => (
                <div key={term} className="grid gap-1 px-6 py-4 sm:grid-cols-3 sm:gap-6">
                  <dt className="text-sm font-semibold text-emerald-300">{term}</dt>
                  <dd className="text-sm leading-relaxed text-white sm:col-span-2">{detail}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      {/* UPL POSITIONING */}
      <section className="mx-auto max-w-3xl px-5 py-16 text-center sm:py-20">
        <p className="text-sm font-bold uppercase tracking-widest text-emerald-600">Built for a regulated profession</p>
        <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900">Della organizes facts. She never practices law.</h2>
        <p className="mt-4 text-lg leading-relaxed text-slate-600">
          Every conversation runs inside hard guardrails written for Colorado's unauthorized-practice
          rules. Della identifies herself as an AI on every call, obtains consent before recording, and
          never collects Social Security numbers, payment details, or full lease documents by voice.
        </p>
      </section>

      {/* WHERE YOUR DATA LIVES — deployment / data-ownership options */}
      <section id="data" className="bg-emerald-50/60 py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-5">
          <SectionHeading
            kicker="Your data, your choice"
            title="Your client's story never lives on someone else's server."
            body="Every intake call, transcript, and brief contains a prospective client's confidential story. Every firm we serve gets its own private server — no shared databases, no co-tenants, ever. The only question is who owns it. We build all three ways."
          />
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            <div className="rounded-2xl border border-emerald-100 bg-white p-7 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900">Managed Private Server</h3>
              <p className="mt-1 text-sm font-semibold italic text-emerald-700">We own it, we run it. Fastest start.</p>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">We provision a private server dedicated to your firm on our infrastructure and run Della on it for you. The only firm's data on that machine is yours, and you can export or delete it at any time.</p>
            </div>
            <div className="rounded-2xl border border-emerald-100 bg-white p-7 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900">Your Own Server</h3>
              <p className="mt-1 text-sm font-semibold italic text-emerald-700">Your account. Your asset.</p>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">The same dedicated setup — but provisioned under your firm's own hosting account. You own the server and the account it lives on; we build, secure, and maintain Della on it. If we part ways, the server, the account, and your data stay yours.</p>
            </div>
            <div className="rounded-2xl border border-emerald-100 bg-white p-7 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900">On-Premises</h3>
              <p className="mt-1 text-sm font-semibold italic text-emerald-700">Full custody.</p>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">For firms that want intake data to never leave the office: Della installed on hardware you own, on your premises. We handle procurement, installation, and setup; you carry the hardware cost. Nothing routes through anyone else's building.</p>
            </div>
          </div>
          <div className="mx-auto mt-10 max-w-3xl rounded-2xl border border-emerald-100 bg-white p-6 text-center shadow-sm">
            <p className="text-sm leading-relaxed text-slate-600">Every option sits behind the same protective layer we run everywhere: all traffic in and out is shielded and encrypted by Cloudflare — the same security network that stands in front of many of the largest sites on the internet — before it ever reaches your server. Attacks, probes, and bad traffic are stopped at the perimeter, not at your data.</p>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">And on every option: recording is off unless you turn it on, callers are asked for consent, and nothing sensitive — Social Security numbers, payment details — is ever collected by voice.</p>
          </div>
        </div>
      </section>

      {/* DEMO CTA */}
      <section id="demo" className="bg-gradient-to-b from-emerald-50/70 to-white py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-5 text-center">
          <p className="text-sm font-bold uppercase tracking-widest text-emerald-600">Hear it yourself</p>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Call Dewy, Beatem &amp; Howe
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-slate-600">
            Our demonstration law firm has practiced fictional law for generations. Its intake technology,
            however, is very real. The firm covers landlord-tenant law, real-estate transactions, and
            property disputes — call about any of them and play a prospective client, or tell Della you're
            an attorney and she'll walk you through how the system would work under your firm's name.
            Every caller goes through the same structured intake, so the attorney always receives a
            complete, well-documented brief.
          </p>
          <div className="mt-8">
            <Link
              to="/contact#form"
              className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 px-8 py-4 text-2xl font-bold text-white shadow-lg transition hover:from-emerald-700 hover:to-teal-700"
            >
              Request Demo Access
            </Link>
          </div>
          <p className="mx-auto mt-6 max-w-xl text-sm leading-relaxed text-slate-500">
            Dewy, Beatem &amp; Howe is a fictional demonstration law firm operated by LeaseCheck. Calls are
            answered by an AI assistant and may be recorded and transcribed with your consent for the
            demonstration. No legal advice is provided and no attorney-client relationship is created.
          </p>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="mx-auto max-w-6xl px-5 pb-20">
        <div className="rounded-3xl bg-slate-900 px-8 py-12 text-center sm:px-16">
          <h2 className="text-3xl font-bold tracking-tight text-white">Put Della on your firm's line</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg leading-relaxed text-white">
            Intake AI is rolling out to LeaseCheck Attorney Network members first — configured for
            your firm name, practice areas, and escalation policies.
          </p>
          <h2 className="mt-8 text-2xl font-bold tracking-tight text-white">Customized Intake Flows are Available</h2>
        </div>
      </section>

    </AttorneyLayout>
  )
}
